import * as THREE from 'three';

export interface CameraConfig {
  camera: THREE.PerspectiveCamera;
  scene: THREE.Scene;
  distance?: number;
  minDistance?: number;
  maxDistance?: number;
  height?: number;
  rotationSpeed?: number;
}

export class ThirdPersonCamera {
  private camera: THREE.PerspectiveCamera;
  private scene: THREE.Scene;
  private target: THREE.Object3D | null = null;

  // Camera positioning
  private distance: number;
  private minDistance: number;
  private maxDistance: number;
  private height: number;

  // Rotation (yaw = horizontal, pitch = vertical)
  private yaw: number = 0;
  private pitch: number = 0.3; // Slight downward angle
  private minPitch: number = -0.5;
  private maxPitch: number = 1.2;

  // Rotation
  private rotationSpeed: number;
  private targetPosition: THREE.Vector3 = new THREE.Vector3();

  // Collision detection
  private raycaster: THREE.Raycaster = new THREE.Raycaster();
  private collisionLayers: THREE.Object3D[] = [];

  // PERF: Reusable vectors to avoid per-frame allocations
  private readonly _lookAtPos: THREE.Vector3 = new THREE.Vector3();
  private readonly _origin: THREE.Vector3 = new THREE.Vector3();
  private readonly _direction: THREE.Vector3 = new THREE.Vector3();
  private readonly _resultPos: THREE.Vector3 = new THREE.Vector3();
  private readonly _forwardVec: THREE.Vector3 = new THREE.Vector3();
  private readonly _rightVec: THREE.Vector3 = new THREE.Vector3();

  // PERF: Cached colliders - only rebuild when scene changes
  private cachedColliders: THREE.Object3D[] = [];
  private colliderCacheTime: number = 0;
  private readonly COLLIDER_CACHE_DURATION: number = 2000; // Rebuild every 2 seconds

  constructor(config: CameraConfig) {
    this.camera = config.camera;
    this.scene = config.scene;
    this.distance = config.distance ?? 8;
    this.minDistance = config.minDistance ?? 3;
    this.maxDistance = config.maxDistance ?? 20;
    this.height = config.height ?? 2;
    this.rotationSpeed = config.rotationSpeed ?? 0.003;
  }

  public setCollisionObjects(objects: THREE.Object3D[]): void {
    this.collisionLayers = objects;
  }

  public setTarget(target: THREE.Object3D): void {
    this.target = target;
  }

  public rotate(deltaX: number, deltaY: number): void {
    this.yaw -= deltaX * this.rotationSpeed;
    this.pitch += deltaY * this.rotationSpeed;

    // Clamp pitch
    this.pitch = Math.max(this.minPitch, Math.min(this.maxPitch, this.pitch));
  }

  public zoom(delta: number): void {
    this.distance += delta * 0.01;
    this.distance = Math.max(this.minDistance, Math.min(this.maxDistance, this.distance));
  }

  public update(_deltaTime: number): void {
    if (!this.target) return;

    const targetPos = this.target.position;

    // Calculate ideal camera position based on yaw and pitch
    const horizontalDistance = this.distance * Math.cos(this.pitch);
    const verticalDistance = this.distance * Math.sin(this.pitch);

    this.targetPosition.set(
      targetPos.x + horizontalDistance * Math.sin(this.yaw),
      targetPos.y + this.height + verticalDistance,
      targetPos.z + horizontalDistance * Math.cos(this.yaw)
    );

    // Camera collision detection - WoW style zoom in when obstructed
    this.checkCameraCollision(targetPos, this.targetPosition, this._resultPos);

    // Set camera position directly (no smoothing, WoW-style)
    this.camera.position.copy(this._resultPos);

    // Look at target (slightly above ground) - reuse vector
    this._lookAtPos.set(
      targetPos.x,
      targetPos.y + this.height * 0.5,
      targetPos.z
    );
    this.camera.lookAt(this._lookAtPos);
  }

  private checkCameraCollision(targetPos: THREE.Vector3, idealPos: THREE.Vector3, result: THREE.Vector3): void {
    // Ray from player to ideal camera position - reuse vectors
    this._origin.set(
      targetPos.x,
      targetPos.y + this.height * 0.5,
      targetPos.z
    );
    this._direction.subVectors(idealPos, this._origin).normalize();
    const maxDistance = this._origin.distanceTo(idealPos);

    this.raycaster.set(this._origin, this._direction);
    this.raycaster.far = maxDistance;

    // Get objects to check collision against - use cached colliders
    const objectsToCheck = this.collisionLayers.length > 0
      ? this.collisionLayers
      : this.getCachedSceneColliders();

    const intersects = this.raycaster.intersectObjects(objectsToCheck, true);

    if (intersects.length > 0) {
      // Found collision - move camera closer
      const hitDistance = intersects[0].distance;
      const safeDistance = Math.max(this.minDistance, hitDistance - 0.5);

      result.set(
        this._origin.x + this._direction.x * safeDistance,
        this._origin.y + this._direction.y * safeDistance,
        this._origin.z + this._direction.z * safeDistance
      );
    } else {
      result.copy(idealPos);
    }
  }

  private getCachedSceneColliders(): THREE.Object3D[] {
    const now = performance.now();

    // Return cached colliders if still valid
    if (now - this.colliderCacheTime < this.COLLIDER_CACHE_DURATION && this.cachedColliders.length > 0) {
      return this.cachedColliders;
    }

    // Rebuild cache
    this.cachedColliders = [];
    const box = new THREE.Box3();
    const size = new THREE.Vector3();

    this.scene.traverse((child) => {
      if (child instanceof THREE.Mesh &&
          child.geometry &&
          !child.userData.ignoreCamera &&
          child.visible) {
        // Ignore small objects, players, enemies, UI elements
        box.setFromObject(child);
        box.getSize(size);
        if (size.x > 2 || size.y > 2 || size.z > 2) {
          this.cachedColliders.push(child);
        }
      }
    });

    this.colliderCacheTime = now;
    return this.cachedColliders;
  }

  public invalidateColliderCache(): void {
    this.colliderCacheTime = 0;
  }

  public getYaw(): number {
    return this.yaw;
  }

  public getPitch(): number {
    return this.pitch;
  }

  public getForwardVector(): THREE.Vector3 {
    // Returns horizontal forward direction based on camera yaw - reuse vector
    this._forwardVec.set(
      -Math.sin(this.yaw),
      0,
      -Math.cos(this.yaw)
    );
    return this._forwardVec;
  }

  public getRightVector(): THREE.Vector3 {
    this._rightVec.set(
      Math.cos(this.yaw),
      0,
      -Math.sin(this.yaw)
    );
    return this._rightVec;
  }
}
